import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useEffect } from "react";
import { Sparkles, Send, CheckCircle2, Wand2, Film, Mic, Layers, Loader2 } from "lucide-react";
import PoweredBy from "../components/PoweredBy";

const TYPES = [
  { key: "flyer", label: "Flyer publicitaire", icon: Wand2 },
  { key: "video", label: "Vidéo courte", icon: Film },
  { key: "voice", label: "Voix off", icon: Mic },
  { key: "composed", label: "Pub complète (visuel + voix + musique)", icon: Layers },
];

export default function BriefSubmit() {
  const { agency_id } = useParams();
  const [agency, setAgency] = useState(null);
  const [loadingAgency, setLoadingAgency] = useState(true);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    client: { name: "", email: "", company: "", phone: "" },
    request_type: "flyer",
    prompt: "",
    details: "",
    budget_estimate: "",
    deadline: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agency/brief/agency/${agency_id}`);
        setAgency(data);
      } catch { setAgency(null); }
      finally { setLoadingAgency(false); }
    })();
  }, [agency_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client.name || !form.client.email || !form.prompt) {
      toast.error("Nom, email et description requis"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/agency/brief", {
        agency_user_id: agency_id,
        client: form.client,
        request_type: form.request_type,
        prompt: form.prompt,
        details: form.details || undefined,
        budget_estimate: form.budget_estimate ? parseFloat(form.budget_estimate) : undefined,
        deadline: form.deadline || undefined,
      });
      setSubmitted(data);
      toast.success("Brief envoyé !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  if (loadingAgency) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }
  if (!agency) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
        <div>
          <div className="text-2xl font-heading">Agence introuvable</div>
          <p className="text-white/60 mt-2">Vérifiez le lien fourni par votre prestataire.</p>
          <Link to="/" className="ghost-btn mt-6 inline-flex">Accueil</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="gold-border-gradient p-10 text-center">
          <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
          <h1 className="mt-4 font-heading text-3xl">Brief envoyé !</h1>
          <p className="mt-3 text-white/70">{submitted.message}</p>
          <div className="mt-6 p-4 bg-gold/5 border border-gold/20 rounded-lg text-left">
            <div className="label-mono mb-2">Lien de suivi (gardez-le)</div>
            <div className="font-mono text-xs text-gold break-all">{submitted.tracking_url || `${window.location.origin}/brief/track/${submitted.access_token}`}</div>
          </div>
          <Link to={`/brief/track/${submitted.access_token}`} data-testid="track-brief-cta" className="gold-btn mt-6 inline-flex">Suivre mon brief</Link>
        </div>
        <PoweredBy variant="inline" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="label-mono">DEMANDE DE CRÉATION PUBLICITAIRE</span>
        </div>
        <h1 className="font-heading text-4xl tracking-tighter">
          Confiez votre <span className="gold-text">brief</span> à <span className="text-gold">{agency.name}</span>
        </h1>
        <p className="text-white/60 mt-3">Décrivez votre besoin, votre agence vous enverra une proposition par email.</p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div className="studio-card p-6">
          <div className="label-mono mb-3">Vos coordonnées</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="brief-name" required value={form.client.name} onChange={e=>setForm({...form, client:{...form.client, name:e.target.value}})}
              placeholder="Votre nom *" className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
            <input data-testid="brief-email" required type="email" value={form.client.email} onChange={e=>setForm({...form, client:{...form.client, email:e.target.value}})}
              placeholder="Email *" className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
            <input data-testid="brief-company" value={form.client.company} onChange={e=>setForm({...form, client:{...form.client, company:e.target.value}})}
              placeholder="Entreprise" className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
            <input data-testid="brief-phone" value={form.client.phone} onChange={e=>setForm({...form, client:{...form.client, phone:e.target.value}})}
              placeholder="Téléphone" className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
          </div>
        </div>

        <div className="studio-card p-6">
          <div className="label-mono mb-3">Quel type de pub ?</div>
          <div className="grid grid-cols-2 gap-3">
            {TYPES.map(t => (
              <button key={t.key} type="button" data-testid={`brief-type-${t.key}`} onClick={()=>setForm({...form, request_type: t.key})}
                className={`p-4 rounded-lg border text-left ${form.request_type===t.key ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
                <t.icon className="w-5 h-5 text-gold mb-2" />
                <div className="font-heading text-sm">{t.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="studio-card p-6">
          <div className="label-mono mb-3">Votre brief</div>
          <textarea data-testid="brief-prompt" required value={form.prompt} onChange={e=>setForm({...form, prompt:e.target.value})}
            placeholder="Décrivez votre publicité : produit/service, public cible, message clé, ambiance, références éventuelles..."
            className="w-full h-32 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none" />
          <textarea data-testid="brief-details" value={form.details} onChange={e=>setForm({...form, details:e.target.value})}
            placeholder="Détails complémentaires (couleurs préférées, logos à intégrer, etc.)"
            className="mt-3 w-full h-20 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none" />
        </div>

        <div className="studio-card p-6">
          <div className="label-mono mb-3">Budget & deadline (optionnel)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="brief-budget" type="number" step="10" min="0" value={form.budget_estimate} onChange={e=>setForm({...form, budget_estimate:e.target.value})}
              placeholder="Budget estimé (€)" className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
            <input data-testid="brief-deadline" type="date" value={form.deadline} onChange={e=>setForm({...form, deadline:e.target.value})}
              className="bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-gold/50" />
          </div>
        </div>

        <button data-testid="brief-submit" type="submit" disabled={loading} className="gold-btn w-full !py-4 disabled:opacity-50">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> Envoyer mon brief</>}
        </button>
      </form>

      <PoweredBy variant="inline" />
    </div>
  );
}
